import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import { computeFieldRequiredState } from './address-schema';
import { FIELD_NAMES } from './constants';
import { commonMessages, messagesByCountry, commonPlaceholders, placeholdersByCountry } from './messages';
import { ChildrenPropType } from '../../prop-types/children';

const AddressForm = React.createContext(null);
export const useAddressFormContext = () => useContext(AddressForm);

export const useAddressField = (fieldName) => {
  const {
    address,
    onChange,
    onBlur,
    errors,
    additionalRequiredFields,
    hiddenFields,
    trackInputChange,
    trackInputClick,
    subLabels,
    overrideRequiredFields,
  } = useAddressFormContext();
  const messageDescriptorsForCountry = messagesByCountry[address.countryCode];
  const placeholderDescriptorsForCountry = placeholdersByCountry[address.countryCode];

  const _onChange = useCallback(
    (value) => {
      onChange(fieldName, value);
    },
    [onChange, fieldName],
  );

  const _onBlur = useCallback(
    (value) => {
      onBlur(fieldName, value);
      trackInputChange(fieldName, value);
    },
    [onBlur, fieldName, trackInputChange],
  );

  const externallyRequired = additionalRequiredFields.includes(fieldName);

  const isRequired = useMemo(
    () => computeFieldRequiredState(fieldName, address?.countryCode, externallyRequired, overrideRequiredFields),
    [fieldName, address?.countryCode, externallyRequired, overrideRequiredFields],
  );

  return {
    value: address[fieldName] || '',
    labelDescriptor: messageDescriptorsForCountry?.[fieldName] || commonMessages[fieldName],
    placeholderDescriptor: placeholderDescriptorsForCountry?.[fieldName] || commonPlaceholders[fieldName],
    onChange: _onChange,
    onBlur: _onBlur,
    address,
    error: errors[fieldName],
    externallyRequired,
    hideField: hiddenFields.includes(fieldName),
    trackInputClick,
    subLabel: subLabels[fieldName],
    isRequired,
  };
};

export const AddressFormContextProvider = ({
  children,
  address,
  onChange = noop,
  onBlur = noop,
  additionalRequiredFields = emptyArray,
  line3 = false,
  state = false,
  errors = emptyObject,
  showAllErrors = false,
  hiddenFields = emptyArray,
  hideOptionalLabels = false,
  disabled = false,
  disableEmail = false,
  type = '',
  trackInputChange = noop,
  trackInputClick = noop,
  recipientInfo = emptyObject,
  subLabels = emptyObject,
  overrideRequiredFields = null,
}) => {
  const value = useMemo(() => {
    return {
      address,
      errors,
      onChange,
      onBlur,
      additionalRequiredFields,
      line3,
      state,
      showAllErrors,
      hiddenFields,
      hideOptionalLabels,
      disabled,
      disableEmail,
      type,
      trackInputChange,
      trackInputClick,
      recipientInfo,
      subLabels,
      overrideRequiredFields,
    };
  }, [
    address,
    errors,
    onChange,
    onBlur,
    additionalRequiredFields,
    line3,
    state,
    showAllErrors,
    hiddenFields,
    hideOptionalLabels,
    disabled,
    disableEmail,
    type,
    trackInputChange,
    trackInputClick,
    recipientInfo,
    subLabels,
    overrideRequiredFields,
  ]);

  return <AddressForm.Provider value={value}>{children}</AddressForm.Provider>;
};

AddressFormContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
  address: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  additionalRequiredFields: PropTypes.arrayOf(PropTypes.oneOf(Object.values(FIELD_NAMES))),
  hiddenFields: PropTypes.arrayOf(PropTypes.oneOf(Object.values(FIELD_NAMES))),
  hideOptionalLabels: PropTypes.bool,
  line3: PropTypes.bool,
  state: PropTypes.bool,
  errors: PropTypes.object,
  showAllErrors: PropTypes.bool,
  disabled: PropTypes.bool,
  disableEmail: PropTypes.bool,
  type: PropTypes.string,
  trackInputChange: PropTypes.func,
  trackInputClick: PropTypes.func,
  recipientInfo: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
  subLabels: PropTypes.object,
  overrideRequiredFields: PropTypes.arrayOf(PropTypes.oneOf(Object.values(FIELD_NAMES))),
};
